import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { SiteSummary } from '@ametica/shared';
import { useSites } from '../api/sites';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { AddSiteDialog } from '../features/sites/AddSiteDialog';
import { SEVERITY, pluralFindings } from '../features/findings/severity';

/** Stav pripojenia: gulička nesie stav, popisok ho pomenúva (title). */
const CONNECTION: Record<SiteSummary['connection'], { color: string; label: string }> = {
  ok: { color: 'bg-ok', label: 'Pripojená' },
  stale: { color: 'bg-sev-medium', label: 'Report je zastaraný' },
  error: { color: 'bg-sev-critical', label: 'Nedostupná' },
  never: { color: 'bg-ghost', label: 'Zatiaľ bez reportu' },
};


/** Zoznam stránok podľa design/Sites.dc.html. */
export function SitesPage() {
  const sites = useSites();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const count = sites.data?.length ?? 0;

  return (
    <>
      <PageTitle
        title="Stránky"
        sub={sites.data ? `${count} monitorovaných inštalácií` : undefined}
        actions={
          <button type="button" className="btn btn-p" onClick={() => setAdding(true)}>
            <Icon name="plus" size={15} />
            Pridať stránku
          </button>
        }
      />

      <div className="card">
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>STRÁNKA</th>
                <th>ZRANITEĽNOSTI</th>
                <th className="whitespace-nowrap">DOSTUPNOSŤ <span className="font-normal tracking-normal text-ghost">v2</span></th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {sites.isPending && <tr><td colSpan={5} className="text-faint">Načítavam…</td></tr>}
              {sites.isError && <tr><td colSpan={5} className="text-sev-critical">Zoznam sa nepodarilo načítať: {sites.error.message}</td></tr>}
              {sites.data?.length === 0 && (
                <tr><td colSpan={5} className="text-faint">Zatiaľ žiadna stránka. Pridaj prvú tlačidlom vpravo hore.</td></tr>
              )}
              {sites.data?.map((s) => {
                const conn = CONNECTION[s.connection];
                return (
                  <tr key={s.id} className="click" onClick={() => navigate(`/sites/${s.id}`)}>
                    <td className="min-w-[220px]">
                      <div className="font-medium">{s.name}</div>
                      <div className="sub">{s.host}</div>
                    </td>
                    <td className="w-[210px]"><FindingsCell site={s} /></td>
                    <td className="mono w-[120px]"><span className="text-[12.5px] text-ghost">—</span></td>
                    <td className="w-11 text-center">
                      <span className={`dotst ${conn.color}`} title={conn.label} aria-label={conn.label} />
                    </td>
                    <td className="w-[34px] text-ghost"><Icon name="chev" size={15} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {adding && <AddSiteDialog onClose={() => setAdding(false)} />}
    </>
  );
}

function FindingsCell({ site }: { site: SiteSummary }) {
  const { open, worst } = site.findings;
  if (open > 0 && worst) {
    // farba nesie najhoršiu závažnosť, popisok ju pomenúva (title) — nikdy farba samotná
    const label = `najhoršia: ${SEVERITY[worst].label.toLowerCase()}`;
    return (
      <span className="flex items-center gap-[9px]" title={label}>
        <span className={`sevsw ${SEVERITY[worst].bg}`} aria-label={label} />
        <span className="font-medium">{open}</span>
        <span className="text-xs text-faint">{pluralFindings(open)}</span>
      </span>
    );
  }
  // nula nálezov z nedosiahnuteľnej alebo ešte nereportujúcej stránky NIE JE čistá stránka
  if (site.connection === 'error') return <span className="text-[12.5px] text-ghost">nevieme — stránka sa neozvala</span>;
  if (site.connection === 'never') return <span className="text-[12.5px] text-ghost">nevieme — zatiaľ bez reportu</span>;
  if (site.connection === 'stale') return <span className="text-[12.5px] text-ghost">nevieme — report je zastaraný</span>;
  return <span className="text-[12.5px] text-faint">žiadne</span>;
}
